import {
  Alert,
  ButtonIcon,
  Card,
  CardBody,
  Table,
  TextField,
  Typography,
} from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import { matchEventsAtom } from '../../../../atoms/matchEvents';
import {
  addSelectedIdAtom,
  selectedVideoReferenceAtom,
} from '../../../../atoms/selectedVideoData';
import CellHeadings from '../../../../components/CellHeadings';
import CardHeader from '../../../../components/CardHeader';
import useApi, { API_ENDPOINTS } from '../../../../hooks/api';
import themeHelper from '../../../../utils/themeHelper';
import { formatTimestamp } from '../../../../utils/time';

const MatchEventsContainer = styled.div`
  padding-top: ${themeHelper.spacing()};
`;

export function MatchEvents() {
  const intl = useIntl();
  const api = useApi();

  const selectedVideoReference = useAtomValue(selectedVideoReferenceAtom);
  const [matchEvents, setMatchEvents] = useAtom(matchEventsAtom);
  const addSelectedId = useSetAtom(addSelectedIdAtom);

  const [message, setMessage] = useState('');

  const [playerFilter, setPlayerFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');
  const [eventTypeNameFilter, setEventTypeNameFilter] = useState('');
  const [playPatternFilter, setPlayPatternFilter] = useState('');

  const emptyOrPresentInValue = (value, filterValue) =>
    (value &&
      value !== '' &&
      value.toUpperCase().includes(filterValue?.toUpperCase())) ||
    value === '';

  const filteredEvents = matchEvents?.filter(
    (event) =>
      event.startTimestamp &&
      emptyOrPresentInValue(event.playerName, playerFilter) &&
      emptyOrPresentInValue(event.teamName, teamFilter) &&
      emptyOrPresentInValue(event.pattern, playPatternFilter) &&
      emptyOrPresentInValue(event.eventType, eventTypeNameFilter),
  );

  const addAllClicked = () => {
    filteredEvents.forEach((event) => addSelectedId(event));
  };

  const refreshEvents = async () => {
    setMessage({
      text: intl.formatMessage({
        defaultMessage: 'Refreshing match events',
        id: 'e23af',
      }),
      type: 'info',
    });
    setMatchEvents([]);
    const response = await api.get(
      // eslint-disable-next-line max-len
      `${API_ENDPOINTS.videoService}/events/FOOTBALL/${selectedVideoReference?.matchId}/${selectedVideoReference?.period}`,
    );
    if (response?.data?.events) {
      const events = response.data.events
        .filter((event) => event.start_timestamp)
        .map((event) => ({
          endTimestamp: event.end_timestamp,
          eventType: event.event_type,
          id: event.id,
          pattern: event.play_pattern,
          playerName: event.player_name,
          startTimestamp: event.start_timestamp,
          teamName: event.team_name || event.possession_team_name,
          timestamp: event.match_timestamp?.split('.')[0],
        }));
      setMatchEvents(events);
      setMessage();
    } else {
      setMessage({
        text: intl.formatMessage({
          defaultMessage: 'Error loading match events',
          id: 'f2297',
        }),
        type: 'error',
      });
    }
  };

  const clearFilters = () => {
    setPlayerFilter('');
    setTeamFilter('');
    setEventTypeNameFilter('');
    setPlayPatternFilter('');
  };

  useEffect(() => {
    if (selectedVideoReference?.matchId && selectedVideoReference?.period) {
      refreshEvents();
      clearFilters();
    }
  }, [selectedVideoReference]);

  return (
    <MatchEventsContainer>
      <Card>
        <CardHeader>
          <FormattedMessage
            defaultMessage="Match Events"
            description="Match Events"
            id="1d09a"
          />
        </CardHeader>
        <CardBody>
          {message && <Alert type={message.type}>{message.text}</Alert>}
          <Table size="small">
            <CellHeadings
              cells={[
                {
                  children: 'Time',
                  id: 'Time',
                },
                {
                  children: 'Type',
                  id: 'Type',
                },
                {
                  children: 'Team',
                  id: 'Team',
                },
                {
                  children: 'Player',
                  id: 'Player',
                },
                {
                  children: 'Play Pattern',
                  id: 'Play',
                },
                {
                  children: '',
                  id: '',
                },
              ]}
              textAlign="left"
            />
            <Table.Body>
              {matchEvents && matchEvents.length > 0 && (
                <Table.Row>
                  <Table.DataCell />
                  <Table.DataCell $textAlign="left">
                    <TextField
                      id="filter-EventTypeName"
                      labelPosition="none"
                      onChange={(event) =>
                        setEventTypeNameFilter(event.target.value)
                      }
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Event Type Filter',
                        id: 'e9f6b',
                      })}
                      size="tiny"
                      value={eventTypeNameFilter}
                      variant="secondary"
                    />
                  </Table.DataCell>
                  <Table.DataCell $textAlign="left">
                    <TextField
                      id="filter-team"
                      labelPosition="none"
                      onChange={(event) => setTeamFilter(event.target.value)}
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Team Filter',
                        id: '1e62d',
                      })}
                      size="tiny"
                      value={teamFilter}
                      variant="secondary"
                    />
                  </Table.DataCell>
                  <Table.DataCell $textAlign="left">
                    <TextField
                      id="filter-player"
                      labelPosition="none"
                      onChange={(event) => setPlayerFilter(event.target.value)}
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Player Filter',
                        id: 'be9ed',
                      })}
                      size="tiny"
                      value={playerFilter}
                      variant="secondary"
                    />
                  </Table.DataCell>
                  <Table.DataCell $textAlign="left">
                    <TextField
                      id="filter-play-pattern"
                      labelPosition="none"
                      onChange={(event) =>
                        setPlayPatternFilter(event.target.value)
                      }
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Play Pattern Filter',
                        id: '9743f',
                      })}
                      size="tiny"
                      value={playPatternFilter}
                      variant="secondary"
                    />
                  </Table.DataCell>
                  <Table.DataCell $textAlign="left">
                    <ButtonIcon
                      icon="PlusAdd"
                      title={intl.formatMessage({
                        defaultMessage: 'Add all to playlist',
                        description: 'Add all to playlist',
                        id: '16e79',
                      })}
                      onClick={() => addAllClicked()}
                      size="small"
                      variant="ghost"
                    />
                  </Table.DataCell>
                </Table.Row>
              )}
              {filteredEvents && filteredEvents.length > 0 ? (
                filteredEvents.map((event) => (
                  <Table.Row
                    key={`event-${event.id}-${event.timestamp}-${event.eventType}`}
                  >
                    <Table.DataCell $textAlign="left">
                      {formatTimestamp(event.timestamp)}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {event.eventType}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {event.teamName}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {event.playerName}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      {event.pattern}
                    </Table.DataCell>
                    <Table.DataCell $textAlign="left">
                      <ButtonIcon
                        icon="PlusAdd"
                        title={intl.formatMessage(
                          {
                            defaultMessage: 'Add {timestamp} to playlist',
                            description: 'Add to playlist',
                            id: 'eff25',
                          },
                          { timestamp: formatTimestamp(event.timestamp) },
                        )}
                        onClick={() => addSelectedId(event)}
                        size="small"
                        variant="ghost"
                      />
                    </Table.DataCell>
                  </Table.Row>
                ))
              ) : (
                <Table.Row>
                  <Table.DataCell $textAlign="left" colSpan={6}>
                    <Typography variant="bodyMedium">
                      <FormattedMessage
                        defaultMessage="Match Events Will Appear Here"
                        id="e1a92"
                      />
                    </Typography>
                  </Table.DataCell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </CardBody>
      </Card>
    </MatchEventsContainer>
  );
}

export default { MatchEvents };
